import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Store, Trash2 } from "lucide-react";

export default function EditLedgerEntryDialog({ open, onOpenChange, customer, entry, onDone }) {
  const [amount, setAmount] = useState("");
  const [shopId, setShopId] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (open && entry) {
      setAmount(String(entry.amount || ""));
      setShopId(entry.shop_id || "");
      setPaymentMode(entry.payment_mode || "");
      setNotes(entry.notes || entry.reason || entry.description || "");
      // Load shops
      api.get("/shops").then(({ data }) => {
        setShops(data.items || []);
      }).catch(() => setShops([]));
    }
  }, [open, entry]);

  const entryId = entry?.id || entry?.reference_id || null;
  const entryIndex = entry?._index; // For legacy entries without ID

  const submit = async () => {
    if (!entryId && entryIndex === undefined) return toast.error("Entry ID not found");
    
    const updates = {};
    if (amount && Number(amount) !== entry.amount) {
      updates.amount = Number(amount);
    }
    if (shopId && shopId !== entry.shop_id) {
      updates.shop_id = shopId;
    }
    if (paymentMode && paymentMode !== entry.payment_mode) {
      updates.payment_mode = paymentMode;
    }
    const originalNotes = entry.notes || entry.reason || entry.description || "";
    if (notes !== originalNotes) {
      updates.notes = notes;
    }
    
    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      onOpenChange(false);
      return;
    }
    
    // Use entry_id if available, otherwise use index
    const identifier = entryId || `index:${entryIndex}`;
    
    setBusy(true);
    try {
      await api.patch(`/credit/${customer.id}/ledger/${identifier}`, updates);
      toast.success("Ledger entry updated");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!entryId && entryIndex === undefined) return toast.error("Entry ID not found");
    const identifier = entryId || `index:${entryIndex}`;
    setBusy(true);
    try {
      await api.delete(`/credit/${customer.id}/ledger/${identifier}`);
      toast.success("Ledger entry deleted");
      setShowDeleteConfirm(false);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const typeLabel = entry?.type?.replace(/_/g, " ").toUpperCase() || "ENTRY";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="edit-ledger-dialog">
          <DialogHeader>
            <DialogTitle>Edit Ledger Entry</DialogTitle>
            <DialogDescription>
              Editing <span className="font-semibold">{typeLabel}</span> entry for{" "}
              <span className="font-medium">{customer?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount *</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 font-mono-data"
                placeholder="0.00"
                data-testid="edit-ledger-amount"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Store className="w-3 h-3" /> Shop
              </Label>
              <Select value={shopId || "__none__"} onValueChange={(v) => setShopId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="edit-ledger-shop">
                  <SelectValue placeholder="Select shop" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No shop —</SelectItem>
                  {shops.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payment Mode</Label>
              <Select value={paymentMode || "__none__"} onValueChange={(v) => setPaymentMode(v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="edit-ledger-mode">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not set —</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes / Description</Label>
              <Textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                className="mt-1" 
                rows={2} 
                placeholder="Optional notes"
                data-testid="edit-ledger-notes"
              />
            </div>
            {entry?.edited_at && (
              <div className="text-[10px] text-muted-foreground border-t pt-2">
                Last edited: {formatDateTime(entry.edited_at)} by {entry.edited_by || "Unknown"}
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowDeleteConfirm(true)} 
              disabled={busy}
              className="mr-auto"
              data-testid="edit-ledger-delete"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !amount} style={{ backgroundColor: "#9E6D5B" }} className="text-white" data-testid="edit-ledger-save">
                {busy ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ledger Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this {typeLabel.toLowerCase()} entry of ₹{Number(entry?.amount || 0).toFixed(2)} from the customer's credit ledger.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
            >
              {busy ? "Deleting…" : "Delete Entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
